// ────────────────────────────────────────────────────────────────────────────
//  api.ts — Frontend-Client für den CustoSoft-Worker
// ────────────────────────────────────────────────────────────────────────────
//
//  Im Dev-Modus geht /api über den Vite-Proxy direkt an den Production-Worker
//  (siehe vite.config.ts). Beim Pages-Deploy wird /api über _redirects
//  ebenfalls dahin proxied — Frontend bleibt domain-agnostisch.
//
//  Auth: JWT-Token wird in localStorage gehalten und auf jeden Request als
//  Bearer-Header gesetzt. Sprache aus localStorage (DE/EN) → Accept-Language.
// ────────────────────────────────────────────────────────────────────────────

// In Production: Direkt-Call zum Worker (Cloudflare Pages _redirects proxiert
// non-GET-Requests nicht zuverlässig). In Dev: relative URL → Vite-Proxy.
// CORS wird im Worker via ALLOWED_ORIGINS / pages.dev-Wildcard erlaubt.
const WORKER_URL = 'https://custosoft-api.davidschroedinger.workers.dev'
const API_BASE   = import.meta.env.DEV ? '/api/v1' : `${WORKER_URL}/api/v1`
const TOKEN_KEY  = 'custosoft.auth.token'
const LANG_KEY   = 'custosoft.lang'

// Auch öffentlich exportieren — z.B. fetch('/api/v1/bugs/upload') in BugReport.tsx
export const API_ORIGIN = import.meta.env.DEV ? '' : WORKER_URL

export class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'APIError'
  }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: unknown
  /// Wenn `true`, wird der Auth-Header NICHT mitgeschickt (Public-Endpoints).
  noAuth?: boolean
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Accept':           'application/json',
    'Accept-Language':  getLanguage(),
  }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (!opts.noAuth) {
    const token = getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(API_BASE + path, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  })

  // 204 No Content → leerer Body
  if (res.status === 204) return undefined as T

  const text = await res.text()
  let payload: any
  try { payload = text ? JSON.parse(text) : {} } catch { payload = { error: text } }

  if (!res.ok) {
    const msg = payload?.error || payload?.message || `HTTP ${res.status}`
    throw new APIError(res.status, msg)
  }
  return payload as T
}

// ── Convenience ──────────────────────────────────────────────────────────────
export const api = {
  get:    <T>(path: string, opts?: Omit<RequestOpts, 'method'|'body'>) =>
            request<T>(path, { ...opts, method: 'GET' }),
  post:   <T>(path: string, body?: unknown, opts?: Omit<RequestOpts, 'method'|'body'>) =>
            request<T>(path, { ...opts, method: 'POST', body }),
  put:    <T>(path: string, body?: unknown, opts?: Omit<RequestOpts, 'method'|'body'>) =>
            request<T>(path, { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: Omit<RequestOpts, 'method'|'body'>) =>
            request<T>(path, { ...opts, method: 'DELETE' }),
}

// ── Token-Speicher ───────────────────────────────────────────────────────────
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else       localStorage.removeItem(TOKEN_KEY)
}

// ── Sprache ──────────────────────────────────────────────────────────────────
export function getLanguage(): string {
  return localStorage.getItem(LANG_KEY) ?? navigator.language ?? 'de'
}
export function setLanguage(lang: 'de' | 'en') {
  localStorage.setItem(LANG_KEY, lang)
}
